import { useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { StyleSheet, Text, View } from 'react-native';

import BrandedButton from '@/components/buttons/BrandedButton';
import DatePickerInputField from '@/components/inputs/DateInputPickerField';
import TextInputField from '@/components/inputs/TextInputField';
import SaveAndContinueLaterButton from '@/components/npo/onboarding/SaveAndContinueLaterButton';
import { RiskProfileData, riskProfileResolver } from '@/lib/validations/NpoPaymentOnboarding';
import { i18n } from '@/utils/i18n';

interface RiskProfileFormProps {
  initialData?: RiskProfileData;
  onDone?: (data: RiskProfileData) => void;
  onSaveAndContinue?: (data: RiskProfileData) => void;
  doneLabel?: string;
  isLoading?: boolean;
}

const RiskProfileForm = ({
  initialData,
  onDone,
  onSaveAndContinue,
  doneLabel = i18n.t('mix.done'),
  isLoading = false
}: RiskProfileFormProps) => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid, errors, isDirty }
  } = useForm<RiskProfileData>({
    defaultValues: initialData,
    mode: 'onChange',
    resolver: riskProfileResolver
  });

  const onSubmit: SubmitHandler<RiskProfileData> = async (data) => {
    if (onDone) {
      onDone(data);
    }
  };

  const onSaveAndContinueHandler: SubmitHandler<RiskProfileData> = async (data) => {
    if (onSaveAndContinue) {
      onSaveAndContinue(data);
    }
  };

  useEffect(() => {
    if (initialData && !isDirty) {
      reset(initialData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData]);

  return (
    <View style={styles.formContainer}>
      <View style={styles.inputsContainer}>
        <TextInputField
          name="averageTransactionAmount"
          control={control}
          label={i18n.t('npoPaymentOnboarding.risk_profile_form.averageTransactionAmount_label')}
          infoSubLabel={i18n.t('npoPaymentOnboarding.risk_profile_form.averageTransactionAmount_info')}
          placeholder={i18n.t('npoPaymentOnboarding.risk_profile_form.averageTransactionAmount_placeholder')}
          returnKeyLabel="next"
          returnKeyType="next"
          type="numeric"
          currency
        />
        <TextInputField
          name="highestTicket"
          control={control}
          label={i18n.t('npoPaymentOnboarding.risk_profile_form.highestTicket_label')}
          infoSubLabel={i18n.t('npoPaymentOnboarding.risk_profile_form.highestTicket_info')}
          placeholder={i18n.t('npoPaymentOnboarding.risk_profile_form.highestTicket_placeholder')}
          returnKeyLabel="next"
          returnKeyType="next"
          type="numeric"
          currency
        />
        <TextInputField
          name="monthlyProcessingVolume"
          control={control}
          label={i18n.t('npoPaymentOnboarding.risk_profile_form.monthlyProcessingVolume_label')}
          infoSubLabel={i18n.t('npoPaymentOnboarding.risk_profile_form.monthlyProcessingVolume_info')}
          placeholder={i18n.t('npoPaymentOnboarding.risk_profile_form.monthlyProcessingVolume_placeholder')}
          returnKeyLabel="next"
          returnKeyType="next"
          type="numeric"
          currency
        />
        <TextInputField
          name="averageCheckAmount"
          control={control}
          label={i18n.t('npoPaymentOnboarding.risk_profile_form.averageCheckAmount_label')}
          infoSubLabel={i18n.t('npoPaymentOnboarding.risk_profile_form.averageCheckAmount_info')}
          placeholder={i18n.t('npoPaymentOnboarding.risk_profile_form.averageCheckAmount_placeholder')}
          returnKeyLabel="next"
          returnKeyType="next"
          type="numeric"
          currency
        />
        <TextInputField
          name="maximumCheckAchAmount"
          control={control}
          label={i18n.t('npoPaymentOnboarding.risk_profile_form.maximumCheckAchAmount_label')}
          infoSubLabel={i18n.t('npoPaymentOnboarding.risk_profile_form.maximumCheckAchAmount_info')}
          placeholder={i18n.t('npoPaymentOnboarding.risk_profile_form.maximumCheckAchAmount_placeholder')}
          returnKeyLabel="next"
          returnKeyType="next"
          type="numeric"
          currency
        />
        <TextInputField
          name="averageNumberOfChecksPerMonth"
          control={control}
          label={i18n.t('npoPaymentOnboarding.risk_profile_form.averageNumberOfChecksPerMonth_label')}
          infoSubLabel={i18n.t('npoPaymentOnboarding.risk_profile_form.averageNumberOfChecksPerMonth_info')}
          placeholder={i18n.t('npoPaymentOnboarding.risk_profile_form.averageNumberOfChecksPerMonth_placeholder')}
          returnKeyLabel="next"
          returnKeyType="next"
          type="numeric"
        />
        <TextInputField
          name="monthlyCheckVolume"
          control={control}
          label={i18n.t('npoPaymentOnboarding.risk_profile_form.monthlyCheckVolume_label')}
          infoSubLabel={i18n.t('npoPaymentOnboarding.risk_profile_form.monthlyCheckVolume_info')}
          placeholder={i18n.t('npoPaymentOnboarding.risk_profile_form.monthlyCheckVolume_placeholder')}
          returnKeyLabel="next"
          returnKeyType="next"
          type="numeric"
          currency
        />
        <DatePickerInputField
          name="businessStartDate"
          control={control}
          label={i18n.t('npoPaymentOnboarding.risk_profile_form.businessStartDate_label')}
          infoSubLabel={i18n.t('npoPaymentOnboarding.risk_profile_form.businessStartDate_info')}
          timePicker={false}
        />
      </View>
      {errors.root !== null && <Text style={styles.errorText}>{errors?.root?.message}</Text>}

      <BrandedButton style={styles.button} loading={isLoading} onPress={handleSubmit(onSubmit)} disabled={!isValid}>
        {doneLabel}
      </BrandedButton>
      <SaveAndContinueLaterButton onPress={handleSubmit(onSaveAndContinueHandler)} />
    </View>
  );
};

const styles = StyleSheet.create({
  formContainer: {
    flex: 1,
    justifyContent: 'space-between'
  },

  inputsContainer: {
    gap: 15
  },

  button: {
    marginTop: 15
  },

  errorText: {
    color: 'red',
    fontSize: 20,
    fontWeight: 'bold'
  }
});

export default RiskProfileForm;
